//  src/utils/moduleScheduler.js
import { useCallback, useEffect, useState } from "react";
import {
  getAnnouncements,
  getCommunityEvents,
  getImages,
  getIqamaScreen,
} from "../services/moduleService";

// NEW: module-level cache helpers (reuse data across iterations)
import { getOrLoadModule } from "../shared/cache/moduleDataCache";

/**
 * Parse duration values coming from config.
 * Accepts:
 *  - number (seconds)
 *  - "m,s"  -> minutes,seconds  (e.g. "0,10" => 10s)
 *  - "m:s"  -> minutes:seconds  (e.g. "0:10")
 *  - "ss"   -> seconds
 * Returns seconds (integer). If parsing fails, returns fallbackSeconds.
 */
function parseDuration(duration, fallbackSeconds = 0) {
  if (duration == null) return fallbackSeconds;
  if (typeof duration === "number") return Math.max(0, Math.floor(duration));
  if (typeof duration !== "string") return fallbackSeconds;

  const s = duration.trim();
  if (!s) return fallbackSeconds;

  // allow comma, colon or semicolon as separator
  const parts = s.split(/[,;:]/).map((p) => p.trim()).filter(Boolean);

  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10) || 0;
    const seconds = parseInt(parts[1], 10) || 0;
    return minutes * 60 + seconds;
  }

  // single number (seconds)
  const n = parseInt(s, 10);
  if (!isNaN(n)) return n;

  return fallbackSeconds;
}

/**
 * Build module tree.
 * Important: your data uses parent_Id that points to *source_Module_Id* of parent.
 * We'll build maps by id and by source_Module_Id and attach children accordingly.
 */
export function buildModuleTree(config) {
  if (!Array.isArray(config)) return [];

  const byId = {};
  const bySourceId = {};
  const roots = [];

  // Create node objects, keep children array
  config.forEach((entry) => {
    const node = { ...entry, children: [] };
    byId[entry.id] = node;
    if (entry.source_Module_Id != null) {
      bySourceId[entry.source_Module_Id] = node;
    }
  });

  // Attach children — parent_Id can reference a parent's source_Module_Id
  config.forEach((entry) => {
    const node = byId[entry.id];
    if (entry.parent_Id != null) {
      const parent = bySourceId[entry.parent_Id] || byId[entry.parent_Id];
      if (parent) {
        parent.children.push(node);
      } else {
        console.warn(
          `[SCHED] buildModuleTree: parent not found for entry id=${entry.id} parent_Id=${entry.parent_Id}`
        );
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // Sort nodes at every level by sequence_No (default 0)
  function sortTree(nodes) {
    nodes.sort((a, b) => (a.sequence_No || 0) - (b.sequence_No || 0));
    nodes.forEach((n) => sortTree(n.children));
  }

  sortTree(roots);
  return roots;
}

/**
 * Hook that schedules outer/inner modules.
 * Returns { currentOuter, currentInner, loadModuleData }
 */
export function useModuleScheduler(config) {
  const [currentOuter, setCurrentOuter] = useState(null);
  const [currentInner, setCurrentInner] = useState(null);

  /**
   * Exposed helper to load data for a single module with cache.
   * Returns whatever the corresponding service returns (array/object/null).
   */
  const loadModuleData = useCallback(async (module) => {
    if (!module?.active) return null;
    const moduleName = (module.source_Module_Name || "").toLowerCase();
    const sourceId = module.source_Module_Id;
    const metadata = {
      id: module.id,
      duration: module.duration,
      active: module.active,
      display_Id: module.display_Id,
      location: module.location,
      sequence_No: module.sequence_No,
    };

    try {
      if (moduleName === "announcements") {
        return await getOrLoadModule(module, async () => {
          const res = await getAnnouncements(sourceId, metadata);
          console.debug(`[SCHED][API] announcements fetched for sourceId=${sourceId}`, {
            sourceId,
            count: Array.isArray(res) ? res.length : 0,
          });
          return res;
        });
      }
      if (moduleName === "events" || moduleName === "communityevents") {
        return await getOrLoadModule(module, async () => {
          const res = await getCommunityEvents(sourceId, metadata);
          console.debug(`[SCHED][API] events fetched for sourceId=${sourceId}`, {
            sourceId,
            count: Array.isArray(res) ? res.length : 0,
          });
          return res;
        });
      }
      if (moduleName === "images") {
        return await getOrLoadModule(module, async () => {
          const res = await getImages(sourceId, metadata);
          console.debug(`[SCHED][API] images fetched for sourceId=${sourceId}`, {
            sourceId,
            count: Array.isArray(res) ? res.length : 0,
          });
          return res;
        });
      }
      if (moduleName === "iqama" || moduleName === "iqamah_screen") {
        // Usually small; still cache for completeness
        return await getOrLoadModule(module, async () => {
          const res = await getIqamaScreen(sourceId, metadata);
          console.debug(`[SCHED][API] iqama screen fetched for sourceId=${sourceId}`);
          return {
            displayName: res?.display_Iqama_Screen_Name || "Horizontal",
            ...res,
          };
        });
      }
    } catch (err) {
      console.error(
        `[SCHED][API ERROR] while fetching ${moduleName} for sourceId=${sourceId}`,
        err
      );
      return null;
    }

    return null;
  }, []);

  useEffect(() => {
    if (!Array.isArray(config) || config.length === 0) {
      setCurrentOuter(null);
      setCurrentInner(null);
      return;
    }

    const tree = buildModuleTree(config);
    //console.log("[SCHED][TREE BUILT]");

    let outerIndex = 0;
    let outerTimer = null;
    let innerTimer = null;
    let stopped = false;

    async function runScheduler() {
      const outerModules = tree.filter((m) => m.active);
      if (outerModules.length === 0) {
        console.warn("[SCHED] No active outer modules found.");
        return;
      }

      async function showNextOuter() {
        if (stopped || outerModules.length === 0) return;
        if (outerIndex >= outerModules.length) outerIndex = 0;

        const outer = outerModules[outerIndex];
        const isIqamah =
          (outer.source_Module_Name || "").toLowerCase() === "iqamah_screen";
        const outerSeconds = parseDuration(outer.duration, 10);

        console.debug(
          `[SCHED] OUTER ${outerIndex}/${outerModules.length - 1} id=${outer.id} name=${outer.source_Module_Name} duration=${outerSeconds}s`
        );

        // Always load outer data first (cached)
        const outerData = await loadModuleData(outer);
        if (stopped) return;

        // Prepare inner list for this outer
        const innerModules = (outer.children || []).filter((c) => c.active);

        // If it's an iqamah screen, PRELOAD ALL active inner modules first.
        // Only after all inner data is ready, reveal the outer.
        let preloadedInner = null;
        if (isIqamah && innerModules.length > 0) {
          const loaded = await Promise.all(
            innerModules.map((m) => loadModuleData(m))
          );
          if (stopped) return;

          // Attach data to a cloned list (keep original nodes untouched)
          preloadedInner = innerModules.map((m, i) => ({
            ...m,
            data: loaded[i],
          }));
        }

        // Reveal outer only after required data is ready
        setCurrentOuter({
          ...outer,
          data: outerData,
          __debug: `OUTER ${outer.source_Module_Name} | duration=${outerSeconds}s`,
        });

        // Stop previous inner loop (if any)
        if (innerTimer) {
          clearTimeout(innerTimer);
          innerTimer = null;
        }
        setCurrentInner(null);

        if (innerModules.length > 0) {
          let innerIndex = 0;

          async function showNextInner() {
            if (stopped) return;
            if (innerModules.length === 0) {
              setCurrentInner(null);
              return;
            }
            if (innerIndex >= innerModules.length) innerIndex = 0;

            const baseInner = innerModules[innerIndex];
            const innerSeconds = parseDuration(baseInner.duration, 2);

            // Use preloaded data for iqamah; otherwise load on demand
            let resolvedInner = baseInner;
            if (isIqamah && preloadedInner) {
              resolvedInner = preloadedInner[innerIndex];
            } else {
              const innerData = await loadModuleData(baseInner);
              if (stopped) return;
              resolvedInner = { ...baseInner, data: innerData };
            }

            setCurrentInner({
              ...resolvedInner,
              __debug: `INNER ${resolvedInner.source_Module_Name} | duration=${innerSeconds}s`,
            });

            innerTimer = setTimeout(() => {
              innerIndex = (innerIndex + 1) % innerModules.length;
              showNextInner();
            }, innerSeconds * 1000);
          }

          // Start inner loop (first one already has data for iqamah)
          await showNextInner();
        } else {
          setCurrentInner(null);
        }

        // Next outer after its full duration
        outerTimer = setTimeout(() => {
          outerIndex = (outerIndex + 1) % outerModules.length;
          showNextOuter();
        }, outerSeconds * 1000);
      }

      // Kick off
      await showNextOuter();
    }

    runScheduler();

    return () => {
      stopped = true;
      if (outerTimer) clearTimeout(outerTimer);
      if (innerTimer) clearTimeout(innerTimer);
    };
    // loadModuleData is stable
  }, [config, loadModuleData]);

  // Expose the cached loader so pages can use it for independent fetches (e.g., footer)
  return { currentOuter, currentInner, loadModuleData };
}
